/**
 * /accounting/gl/detail — drill-down GL detail for one account.
 *
 * `?account=<id>` selects the account; defaults to first cash account.
 */
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) => n === 0 ? "—" : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const sfmt = (n: number) => `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function GeneralLedgerDetailPage() {
  const { accounts, accountById, getAccountLedger } = useGeneralLedger();
  const [params, setParams] = useSearchParams();
  const selected = params.get("account") || accounts.find(a => a.subtype === "cash")?.id || accounts[0]?.id;

  const rows = useMemo(
    () => selected ? getAccountLedger(selected) : [],
    [selected, getAccountLedger],
  );
  const account = selected ? accountById.get(selected) : undefined;
  const totDr = rows.reduce((s, r) => s + (r.line.side === "debit" ? r.line.amount : 0), 0);
  const totCr = rows.reduce((s, r) => s + (r.line.side === "credit" ? r.line.amount : 0), 0);
  const ending = rows[0]?.runningBalance ?? 0;

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">General Ledger Detail</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every posting that touched this account. Source documents linked where available.
          </p>
        </div>
        <div className="w-80">
          <Select value={selected ?? undefined} onValueChange={v => setParams({ account: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-96">
              {[...accounts].sort((a, b) => a.number.localeCompare(b.number)).map(a => (
                <SelectItem key={a.id} value={a.id} className="text-xs">
                  <span className="font-mono mr-2">{a.number}</span>{a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {account ? (
        <>
          <CockpitSection>
            <div className="rounded-md border border-border bg-card p-4 grid grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Account</div>
                <div className="font-medium"><span className="font-mono text-xs mr-2">{account.number}</span>{account.name}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Debits</div>
                <div className="font-semibold tabular-nums">{fmt(totDr)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Credits</div>
                <div className="font-semibold tabular-nums">{fmt(totCr)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Ending Balance ({account.normalBalance === "debit" ? "DR normal" : "CR normal"})</div>
                <div className={`font-semibold tabular-nums ${ending < 0 ? "text-destructive" : ""}`}>{sfmt(ending)}</div>
              </div>
            </div>
          </CockpitSection>

          <CockpitSection>
            <div className="rounded-md border border-border bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left w-24">Date</th>
                    <th className="px-3 py-2 text-left w-36">JE #</th>
                    <th className="px-3 py-2 text-left">Description / Subledger</th>
                    <th className="px-3 py-2 text-right w-28">Debit</th>
                    <th className="px-3 py-2 text-right w-28">Credit</th>
                    <th className="px-3 py-2 text-right w-32">Running</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">No activity on this account.</td></tr>
                  ) : rows.map(({ entry, line, runningBalance }) => (
                    <tr key={line.id} className="border-t border-border">
                      <td className="px-3 py-1.5 text-xs">{entry.postingDate}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">
                        <Link to={`/accounting/gl/journal-entries?focus=${entry.id}`} className="text-primary hover:underline">{entry.number}</Link>
                      </td>
                      <td className="px-3 py-1.5">
                        <div>{entry.description}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {[line.memo, line.customerId && `cust:${line.customerId}`, line.vendorId && `vend:${line.vendorId}`, line.jobId && `WO#${line.jobId}`, line.tradeType]
                            .filter(Boolean).join(" · ")}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{line.side === "debit" ? fmt(line.amount) : ""}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{line.side === "credit" ? fmt(line.amount) : ""}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{sfmt(runningBalance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CockpitSection>
        </>
      ) : null}
    </CockpitPage>
  );
}
